import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import type { CorrectiveAction, CorrectiveActionStatus } from '../lib/types.ts';

const STATUS_META: Record<CorrectiveActionStatus, { label: string; cls: string }> = {
  open: { label: 'Awaiting company', cls: 'bg-amber-50 text-amber-700' },
  responded: { label: 'Responded — review', cls: 'bg-blue-50 text-blue-700' },
  approved: { label: 'Approved', cls: 'bg-emerald-50 text-emerald-700' },
  rejected: { label: 'Rejected', cls: 'bg-red-50 text-red-700' },
};

const TABS: { key: CorrectiveActionStatus | 'all'; label: string }[] = [
  { key: 'responded', label: 'To review' },
  { key: 'open', label: 'Awaiting company' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
];

function assetLabel(ca: CorrectiveAction): string {
  const v = ca.asset_audit?.asset;
  return (
    [v?.year, v?.make, v?.model].filter(Boolean).join(' ') || v?.registration_plate || 'asset'
  );
}

export function AdminCorrectiveActions() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<CorrectiveActionStatus | 'all'>('responded');

  const { data, isLoading } = useQuery({
    queryKey: ['corrective-actions', tab],
    queryFn: () =>
      adminApi.get<{ corrective_actions: CorrectiveAction[] }>('/api/corrective-actions', {
        query: tab === 'all' ? {} : { status: tab },
      }),
  });

  const review = useMutation({
    mutationFn: (input: { id: string; decision: 'approve' | 'reject' }) =>
      adminApi.patch(`/api/corrective-actions/${input.id}`, { decision: input.decision }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['corrective-actions'] }),
  });

  const items = data?.corrective_actions ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Corrective actions</h1>
        <p className="mt-1 text-sm text-slate-500">
          Items raised from failed asset audits. The company responds with a comment and optional
          photo; you approve or send it back.
        </p>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`border-b-2 px-3 py-2 text-sm transition ${
              tab === t.key
                ? 'border-blue-600 font-semibold text-blue-700'
                : 'border-transparent font-medium text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <CenteredSpinner />
      ) : items.length === 0 ? (
        <EmptyState title="Nothing here" description="No corrective actions match this filter." />
      ) : (
        <div className="space-y-3">
          {items.map((ca) => {
            const meta = STATUS_META[ca.status];
            return (
              <div key={ca.id} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">{ca.question_label}</span>
                      <span className={`rounded-full px-2 py-0.5 text-xs ${meta.cls}`}>{meta.label}</span>
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {ca.company?.company_name} · {assetLabel(ca)}
                    </div>
                    {ca.description && <div className="mt-1 text-sm text-slate-700">{ca.description}</div>}
                  </div>
                </div>

                {ca.company_comment && (
                  <div className="mt-3 rounded-md bg-slate-50 p-3 text-sm">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Company response
                    </div>
                    <div className="mt-1 text-slate-700">{ca.company_comment}</div>
                    {ca.company_photo_url && (
                      <img src={ca.company_photo_url} alt="" className="mt-2 h-28 rounded object-cover" />
                    )}
                  </div>
                )}

                {ca.status === 'responded' && (
                  <div className="mt-3 flex gap-2">
                    <Btn
                      size="sm"
                      onClick={() => review.mutate({ id: ca.id, decision: 'approve' })}
                      loading={review.isPending && review.variables?.id === ca.id}
                    >
                      Approve
                    </Btn>
                    <Btn
                      size="sm"
                      variant="ghost"
                      onClick={() => review.mutate({ id: ca.id, decision: 'reject' })}
                    >
                      Send back
                    </Btn>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {review.error && <ErrorMessage error={review.error} />}
    </div>
  );
}
